import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { BUSINESS_TYPES, DEFAULT_INTERVALS } from "@/lib/businessTypes";
import { Plus, Trash, PencilSimple, WhatsappLogo, CheckCircle, Sparkle, MagnifyingGlass, Upload, PaperPlaneTilt } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  business_type: "",
  last_service_date: new Date().toISOString().slice(0, 10),
  interval_days: 90,
  notes: "",
  last_service_km: "",
  interval_km: "",
  shop_name: "",
  whatsapp_number: "",
};

const KM_TRACKED = ["Car Garage", "Bike Service"];
const DEFAULT_KM = { "Car Garage": 10000, "Bike Service": 3000 };

// Strip country prefix + non-digits from stored phone → 10-digit input value.
const to10Digit = (s) => {
  if (!s) return "";
  let d = String(s).replace(/\D/g, "");
  if (d.startsWith("91") && d.length === 12) d = d.slice(2);
  if (d.startsWith("0") && d.length === 11) d = d.slice(1);
  return d.slice(0, 10);
};

// Input that shows a fixed "+91" badge and accepts only 10 digits.
function PhoneInputIN({ value, onChange, testid, placeholder = "10-digit mobile" }) {
  return (
    <div className="flex items-stretch nb-input p-0 overflow-hidden focus-within:ring-2 focus-within:ring-[#4361EE]/40">
      <span className="flex items-center px-3 bg-[#F3F0E9] border-r-2 border-black font-mono text-sm font-bold select-none">
        +91
      </span>
      <input
        data-testid={testid}
        inputMode="numeric"
        pattern="[0-9]{10}"
        maxLength={10}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
        placeholder={placeholder}
        className="flex-1 px-3 py-2 bg-transparent outline-none font-mono text-sm"
      />
    </div>
  );
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHint, setAiHint] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/customers");
    setCustomers(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setAiHint("");
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, phone: to10Digit(c.phone), email: c.email || "",
      business_type: c.business_type,
      last_service_date: c.last_service_date,
      interval_days: c.interval_days,
      notes: c.notes || "",
      last_service_km: c.last_service_km ?? "",
      interval_km: c.interval_km ?? "",
      shop_name: c.shop_name ?? "",
      whatsapp_number: to10Digit(c.whatsapp_number),
    });
    setAiHint("");
    setDialogOpen(true);
  };

  const submit = async () => {
    try {
      if (!form.name || !form.phone || !form.business_type) {
        toast.error("Name, phone, and business type are required");
        return;
      }
      if (form.phone.length !== 10) {
        toast.error("Enter a valid 10-digit mobile number");
        return;
      }
      const payload = { ...form };
      // Compose full E.164 for backend: user typed 10 digits, we prefix +91
      payload.phone = "+91" + form.phone;
      payload.whatsapp_number = form.whatsapp_number ? "+91" + form.whatsapp_number : "";
      // Convert km fields: empty string -> null, otherwise int
      payload.last_service_km = form.last_service_km === "" ? null : parseInt(form.last_service_km);
      payload.interval_km = form.interval_km === "" ? null : parseInt(form.interval_km);
      if (editing) {
        await api.patch(`/customers/${editing.customer_id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Deleted");
    load();
  };

  const send = async (id, channel, mode = "text") => {
    try {
      const { data } = await api.post("/reminders/send", { customer_id: id, channel, mode });
      if (data?.ok) {
        toast.success(data.mocked ? `WhatsApp queued (MOCKED)` : `WhatsApp ${mode === "template" ? "template " : ""}sent`);
      } else {
        toast.error(`WhatsApp send failed (${data?.meta_code || "unknown"})`, {
          description: data?.hint || data?.meta_message || "Meta rejected the send. Check API Setup.",
          duration: 15000,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
  };

  const markServiced = async (id) => {
    const cust = customers.find((c) => c.customer_id === id);
    const isKm = cust && KM_TRACKED.includes(cust.business_type);
    let service_km;
    if (isKm) {
      const v = window.prompt(`Enter current odometer reading for ${cust.name} (km):`, cust.last_service_km ?? "");
      if (v === null) return;
      const n = parseInt(v);
      if (!isNaN(n)) service_km = n;
    }
    await api.post("/customers/mark-serviced", { customer_id: id, service_km });
    toast.success("Marked as serviced — next date updated");
    load();
  };

  const onBizChange = (v) => {
    setForm((f) => {
      const next = { ...f, business_type: v, interval_days: DEFAULT_INTERVALS[v] || f.interval_days };
      if (KM_TRACKED.includes(v)) {
        if (!next.interval_km) next.interval_km = DEFAULT_KM[v];
      } else {
        next.last_service_km = "";
        next.interval_km = "";
      }
      return next;
    });
    setAiHint("");
  };

  const suggestAI = async () => {
    if (!form.business_type) {
      toast.error("Pick a business type first");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/suggest-interval", { business_type: form.business_type });
      setForm((f) => ({ ...f, interval_days: data.interval_days }));
      setAiHint(`AI: ${data.interval_days} days — ${data.rationale || ""}`);
    } catch {
      toast.error("AI suggestion failed");
    } finally {
      setAiLoading(false);
    }
  };

  const onBizChangeUnused = null; // (removed)

  const filtered = customers.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name.toLowerCase().includes(s) || c.phone.includes(s) || c.business_type.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">Customer base</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Customers</h1>
        </div>
        <div className="flex gap-2 self-start">
          <label data-testid="csv-import-label" className="nb-btn bg-white px-4 py-3 flex items-center gap-2 cursor-pointer">
            <Upload size={18} weight="bold" /> Import CSV
            <input
              data-testid="csv-import-input"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append("file", f);
                try {
                  const { data } = await api.post("/customers/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success(`Imported ${data.inserted} customer(s)${data.errors.length ? `. ${data.errors.length} row error(s).` : ""}`);
                  if (data.errors.length) console.warn(data.errors);
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Import failed");
                }
                e.target.value = "";
              }}
            />
          </label>
          <button data-testid="add-customer-button" onClick={openAdd} className="nb-btn bg-[#4361EE] text-white px-5 py-3 flex items-center gap-2">
            <Plus size={18} weight="bold" /> Add Customer
          </button>
        </div>
      </div>

      <div className="nb-card p-3 flex items-center gap-2">
        <MagnifyingGlass size={18} weight="bold" className="ml-2" />
        <input
          data-testid="search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, phone, or business type…"
          className="flex-1 bg-transparent outline-none font-semibold py-2 px-1"
        />
      </div>

      <div className="nb-card overflow-x-auto">
        <table className="w-full" data-testid="customers-table">
          <thead className="bg-[#F3F0E9] border-b-2 border-black">
            <tr className="text-left">
              <Th>Name</Th>
              <Th>Business Type</Th>
              <Th>Phone</Th>
              <Th>Last Service</Th>
              <Th>Next Service</Th>
              <Th className="text-right pr-5">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center font-semibold">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center font-semibold text-[#4A4A4A]">
                {customers.length === 0 ? "No customers yet. Click Add Customer to start." : "No matches."}
              </td></tr>
            ) : filtered.map((c) => (
              <tr key={c.customer_id} className="border-b border-black/10 hover:bg-[#F3F0E9]" data-testid={`row-${c.customer_id}`}>
                <Td className="font-bold">{c.name}</Td>
                <Td><span className="text-xs font-bold px-2 py-1 border-2 border-black rounded-full bg-[#4ECDC4]/40">{c.business_type}</span></Td>
                <Td className="font-mono text-sm">{c.phone}</Td>
                <Td className="text-sm">{c.last_service_date}{c.last_service_km != null ? <span className="block text-xs font-mono text-[#4A4A4A]">{c.last_service_km.toLocaleString()} km</span> : null}</Td>
                <Td className="text-sm font-bold">{c.next_service_date}{c.next_service_km != null ? <span className="block text-xs font-mono text-[#4361EE]">{c.next_service_km.toLocaleString()} km</span> : null}</Td>
                <Td className="text-right pr-5">
                  <div className="flex justify-end gap-1.5">
                    <IconBtn title="Send WhatsApp reminder (uses approved template — works to any customer)" onClick={() => send(c.customer_id, "whatsapp", "template")} testid={`send-wa-${c.customer_id}`}>
                      <WhatsappLogo size={16} weight="bold" />
                    </IconBtn>
                    <IconBtn title="Mark Serviced" onClick={() => markServiced(c.customer_id)} testid={`mark-${c.customer_id}`}>
                      <CheckCircle size={16} weight="bold" />
                    </IconBtn>
                    <IconBtn title="Edit" onClick={() => openEdit(c)} testid={`edit-${c.customer_id}`}>
                      <PencilSimple size={16} weight="bold" />
                    </IconBtn>
                    <IconBtn title="Delete" onClick={() => remove(c.customer_id)} testid={`delete-${c.customer_id}`}>
                      <Trash size={16} weight="bold" />
                    </IconBtn>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="border-2 border-black shadow-[6px_6px_0px_#111111] rounded-md bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">
              {editing ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
            <DialogDescription>Fill in the customer's details and service interval.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Field label="Full Name">
              <Input data-testid="form-name" className="nb-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Shop / Company (optional)">
              <Input data-testid="form-shop" className="nb-input" value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} placeholder="e.g. Ravi Distributors" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <PhoneInputIN testid="form-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              </Field>
              <Field label="WhatsApp # (opt)">
                <PhoneInputIN testid="form-wa" value={form.whatsapp_number} onChange={(v) => setForm({ ...form, whatsapp_number: v })} placeholder="Defaults to Phone" />
              </Field>
            </div>
            <Field label="Email (optional)">
              <Input data-testid="form-email" className="nb-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Business Type">
              <Select value={form.business_type} onValueChange={onBizChange}>
                <SelectTrigger data-testid="form-biz-type" className="nb-input">
                  <SelectValue placeholder="Choose business type" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((b) => (<SelectItem key={b} value={b}>{b}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Last Service Date">
                <Input data-testid="form-last-date" type="date" className="nb-input" value={form.last_service_date} onChange={(e) => setForm({ ...form, last_service_date: e.target.value })} />
              </Field>
              <Field label="Interval (days)">
                <div className="flex gap-2">
                  <Input data-testid="form-interval" type="number" min="1" className="nb-input" value={form.interval_days} onChange={(e) => setForm({ ...form, interval_days: parseInt(e.target.value || "0") })} />
                  <button data-testid="ai-suggest-btn" type="button" onClick={suggestAI} disabled={aiLoading} className="nb-btn bg-[#FFD166] px-3 flex items-center gap-1 text-xs">
                    <Sparkle size={14} weight="fill" />{aiLoading ? "..." : "AI"}
                  </button>
                </div>
              </Field>
            </div>
            {aiHint && <p data-testid="ai-hint" className="text-xs font-semibold text-[#4361EE]">{aiHint}</p>}
            {KM_TRACKED.includes(form.business_type) && (
              <div className="grid grid-cols-2 gap-3 border-2 border-dashed border-black/30 rounded-md p-3 bg-[#4ECDC4]/10">
                <div className="col-span-2 text-xs font-bold tracking-[0.15em] uppercase text-[#111111]">
                  Kilometre tracking ({form.business_type})
                </div>
                <Field label="Last Service KM">
                  <Input
                    data-testid="form-last-km"
                    type="number"
                    min="0"
                    className="nb-input"
                    placeholder="e.g. 45000"
                    value={form.last_service_km}
                    onChange={(e) => setForm({ ...form, last_service_km: e.target.value })}
                  />
                </Field>
                <Field label="Interval (km)">
                  <Input
                    data-testid="form-interval-km"
                    type="number"
                    min="0"
                    className="nb-input"
                    placeholder={form.business_type === "Car Garage" ? "10000" : "3000"}
                    value={form.interval_km}
                    onChange={(e) => setForm({ ...form, interval_km: e.target.value })}
                  />
                </Field>
                {form.last_service_km !== "" && form.interval_km !== "" && (
                  <p className="col-span-2 text-xs font-semibold text-[#4A4A4A]">
                    Next service at:&nbsp;
                    <span className="font-mono font-bold text-[#111111]">
                      {(parseInt(form.last_service_km || 0) + parseInt(form.interval_km || 0)).toLocaleString()} km
                    </span>
                  </p>
                )}
              </div>
            )}
            <Field label="Notes">
              <Textarea data-testid="form-notes" className="nb-input min-h-[70px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <button data-testid="form-cancel" onClick={() => setDialogOpen(false)} className="nb-btn bg-white px-4 py-2">Cancel</button>
            <button data-testid="form-save" onClick={submit} className="nb-btn bg-[#4361EE] text-white px-4 py-2">
              {editing ? "Save Changes" : "Add Customer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-xs font-bold tracking-[0.15em] uppercase ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-bold tracking-[0.15em] uppercase mb-1.5 block">{label}</Label>
    {children}
  </div>
);
const IconBtn = ({ children, onClick, title, testid }) => (
  <button data-testid={testid} title={title} onClick={onClick} className="w-8 h-8 border-2 border-black rounded-md bg-white hover:bg-[#FFD166] flex items-center justify-center transition-colors">
    {children}
  </button>
);
